<script setup lang="ts">
import { computed, ref } from "vue";
import { RouterLink } from "vue-router";
import { useAppCore } from "@lunagoai/vue-app-core";
import BaseButton from "@lunagoai/vue-ui/components/BaseButton.vue";
import BasePopoverButton from "@lunagoai/vue-ui/components/BasePopoverButton.vue";
import { type ApiPrincipal } from "../../api";
import { messages, type Locale } from "../../i18n";
import { sessionState } from "../../app/session";
import ChangePasswordDialog from "../account/ChangePasswordDialog.vue";

const props = withDefaults(defineProps<{ collapsed?: boolean }>(), { collapsed: false });
const emit = defineEmits<{ logout: [] }>();
const appCore = useAppCore();
const language = appCore.language!;
const theme = appCore.theme!;
const principal = computed<ApiPrincipal | null>(() => sessionState().principal);
const accountOpen = ref(false);
const passwordDialogVisible = ref(false);
const locale = computed(() => language.currentLanguage.value as Locale);
const themeMode = computed(() => theme.theme.value);
const isAdmin = computed(() => principal.value?.roles.includes("admin") || principal.value?.roles.includes("operator"));
const t = (key: keyof typeof messages["zh-TW"]) => messages[locale.value][key];
const displayName = computed(() => principal.value?.username ?? t("account"));
const userInitial = computed(() => displayName.value.trim().slice(0, 1).toUpperCase() || "U");

function changeTheme(value: "light" | "dark" | "system"): void {
  theme.setTheme(value);
}

function closeMenu(): void {
  accountOpen.value = false;
}

function openAccount(): void {
  closeMenu();
}

function openPasswordDialog(): void {
  closeMenu();
  passwordDialogVisible.value = true;
}

function signOut(): void {
  closeMenu();
  emit("logout");
}
</script>

<template>
  <div class="relative p-2.5 pt-2">
    <BasePopoverButton
      v-model:visible="accountOpen"
      variant="transparent"
      block
      align="start"
      display="flex"
      :show-chevron="!props.collapsed"
      has-popup="menu"
      placement="top-end"
      popover-width="w-64"
      popover-class-name="border-ui bg-surface text-ui shadow-2xl"
      popover-padding="p-2.5"
      :class="props.collapsed ? 'min-h-12 !justify-center !rounded-lg !border !border-slate-800 !bg-slate-900 !px-1 !py-2 !text-slate-100 hover:!border-cyan-500/40 hover:!bg-cyan-500/10' : 'min-h-12 w-full !gap-2.5 !rounded-lg !border !border-slate-800 !bg-slate-900 !px-2.5 !py-2 !text-slate-100 hover:!border-cyan-500/40 hover:!bg-cyan-500/10'"
      :aria-label="displayName"
    >
      <span class="grid h-[28px] w-[28px] shrink-0 place-items-center rounded-full bg-cyan-500/15 text-xs font-black text-cyan-300">{{ userInitial }}</span>
      <span v-if="!props.collapsed" class="grid min-w-0 flex-1 text-left">
        <strong class="truncate text-[13px]">{{ displayName }}</strong>
        <small class="text-[11px] text-slate-500">{{ isAdmin ? t("administrator") : t("customer") }}</small>
      </span>
      <template #popover>
        <div class="grid gap-2" role="menu" :aria-label="displayName">
          <div class="grid gap-1">
            <span class="px-1 text-xs font-semibold text-ui-muted">{{ t("theme") }}</span>
            <div class="grid grid-cols-3 gap-1">
              <BaseButton :label="t('themeLight')" :variant="themeMode === 'light' ? 'accent-outline' : 'ghost'" size="sm" block role="menuitem" @click="changeTheme('light')" />
              <BaseButton :label="t('themeDark')" :variant="themeMode === 'dark' ? 'accent-outline' : 'ghost'" size="sm" block role="menuitem" @click="changeTheme('dark')" />
              <BaseButton :label="t('themeSystem')" :variant="themeMode === 'system' ? 'accent-outline' : 'ghost'" size="sm" block role="menuitem" @click="changeTheme('system')" />
            </div>
          </div>
          <RouterLink to="/app/account" class="flex min-h-9 w-full items-center rounded-lg border border-ui bg-transparent px-3 py-1.5 text-sm font-semibold text-ui no-underline transition-colors hover:bg-primary/15 hover:text-primary-light focus:outline-none focus:ring-2 focus:ring-primary/40" role="menuitem" @click="openAccount">
            {{ t("account") }}
          </RouterLink>
          <BaseButton label="變更密碼" variant="secondary" size="sm" block class="justify-start" role="menuitem" @click="openPasswordDialog" />
          <BaseButton :label="t('logout')" icon="LogOut" variant="secondary" size="sm" block class="justify-start" role="menuitem" @click="signOut" />
        </div>
      </template>
    </BasePopoverButton>
    <ChangePasswordDialog v-model:visible="passwordDialogVisible" />
  </div>
</template>
